import {
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Button, Eyebrow, Heading, Stripe, Text } from '../ui/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { BackdropProvider } from '../components/layout/Wallpaper.js';
import { Focusable, HintsProvider } from '../components/focus/Focusable.js';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

function RootLayout() {
  return (
    <HintsProvider>
      <BackdropProvider>
        <AppShell>
          <Outlet />
        </AppShell>
      </BackdropProvider>
    </HintsProvider>
  );
}

/**
 * Nothing here.
 *
 * A dead end still sounds like the studio: it says what happened and where to go, rather than
 * printing a number at someone.
 */
function NotFound() {
  return (
    <div className="gb-center">
      <Stripe />
      <Eyebrow tone="quiet">{'404'}</Eyebrow>
      <Heading size="5">{'That one is not on the shelf.'}</Heading>
      <Text color="gray" style={{ maxWidth: '36ch' }}>
        {'The address does not lead anywhere. Try the rail on the left.'}
      </Text>
    </div>
  );
}

function RouteError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="gb-center">
      <Stripe />
      <Eyebrow tone="quiet">{'Something broke'}</Eyebrow>
      <Heading size="5">{'That did not work.'}</Heading>
      <Text color="gray" style={{ maxWidth: '44ch' }}>
        {error instanceof Error ? error.message : String(error)}
      </Text>
      {/* The one thing to do on this screen, so it is what the controller lands on. */}
      <Focusable autoFocus onSelect={reset} selectLabel="Try again" className="gb-focusable--flat">
        <Button size="3">{'Try again'}</Button>
      </Focusable>
    </div>
  );
}
